import React, { ReactNode, useEffect, useState } from "react"
import styles from "@css/modules/ListingPricing.module.scss"
import { addCurrency } from "@utils/index"
import { getTotalPrice } from "../listings-page-utils"
import { PointsAvailability } from "../PointsAvailability"
import { OwnerImage } from "./components/Owner"
import cn from "classnames"
import moment from "moment"
import { originNames } from "@utils/const"
import { Listing } from "@t/Listing"
import { BidSuggestion } from "../../../app/listings/[id]/components/BidSuggestion/BidSuggestion"
import { BugReport } from "../../../app/listings/[id]/components/BugReport"

/*const brokerMap = {
    dvcshop: "resales.dvcshop.com",
    dvcstore: "www.dvcstore.com",
    dvcresalemarket: "www.dvcresalemarket.com",
    fidelityresales: "www.fidelityresales.com"
}*/

function getPriceDescription(closingCost: number, annualDues: number) {
    if (closingCost && annualDues) {
        return null
    }
    if (!closingCost && !annualDues) {
        return "+ Closing costs and dues reimbursement"
    }
    if (!closingCost) {
        return "+ Closing costs"
    }
    if (!annualDues) {
        return "+ Dues reimbursement"
    }
}

const classMap = {
    headerWrapper: styles.headerWrapper,
    daysOnMarket: "",
    btnWrap: styles.btnWrap,
    title: styles.title,
    priceList: styles.priceList,
    hr: styles.hr,
    total: styles.total,
    priceDesc: styles.priceDesc,
    onMarket: styles.onMarket
}

type Props = {
    listing: Listing
    headerContent?: ReactNode
    withoutBtn?: boolean
    withBidSuggestion?: boolean
    title?: string
    className?: string
    classes?: Partial<typeof classMap>
}

export function ListingPricing({ listing, withoutBtn, withBidSuggestion, title = "Pricing", className, classes, headerContent }: Props) {
    const [open, setOpen] = useState(false)

    const use_month = listing.use_month || ""
    const deed_exp = listing.deed_exp || ""
    const totalCost = getTotalPrice(listing)
    const closingCost = listing.closing_cost
    const annualDues = listing.dues_reimbursement
    const priceDesc = getPriceDescription(closingCost, annualDues)
    const url = new URL(listing.link)
    const name = url.hostname.replace(/^www\./, "")
    const created = moment(listing.parsed_date)
    const days = moment().diff(moment(listing.parsed_date), "days")
    const suffix = days === 1 ? " day" : " days"
    const css = {
        ...classMap,
        ...classes
    }
    const [isBugReportEnabled, setIsBugReportEnabled] = useState(false)
    useEffect(() => {
        const isBugReportEnabled = localStorage.getItem("bugReportEnabled")
        try {
            const values = JSON.parse(isBugReportEnabled)
            if (values?.[listing.id]) {
                setIsBugReportEnabled(true)
            }
        } catch (e) {}
    }, [])
    const onPurchaseClick = () => {
        const storeValue = localStorage.getItem("bugReportEnabled")
        let values = {}
        if (storeValue) {
            try {
                values = JSON.parse(storeValue)
            } catch (e) {}
        }
        values[listing.id] = 1
        localStorage.setItem("bugReportEnabled", JSON.stringify(values))
        setIsBugReportEnabled(true)
    }
    return (
        <section className={cn(className, styles.root)}>
            <div className={css.headerWrapper}>
                {headerContent}
                <div className={css.onMarket}>
                    Listed on {created.format("MMM Do, YYYY")},
                    <div className={cn(css.daysOnMarket)}>
                        {days}
                        {suffix} on market
                    </div>
                </div>
            </div>
            <h3 className={css.title}>{title}</h3>
            <ul className={css.priceList}>
                <li>
                    Points <span>{listing.points_on_contract}</span>
                </li>
                <li>
                    Price Per Point <span>{addCurrency(listing.price_per_point)}</span>
                </li>
                {closingCost && (
                    <li>
                        Closing Cost
                        <span>{addCurrency(closingCost)}</span>
                    </li>
                )}
                {annualDues && (
                    <li>
                        Annual Dues Reimbursement <span>{addCurrency(annualDues)}</span>
                    </li>
                )}
                <li>
                    Use Year <span>{use_month}</span>
                </li>
                <li>
                    Deed Expiration <span>{deed_exp}</span>
                </li>
                <li className={css.hr} />
                <li className={css.total}>
                    Total* <span>{addCurrency(totalCost)}</span>
                </li>
                {priceDesc && <li className={css.priceDesc}>{priceDesc}</li>}
            </ul>
            <PointsAvailability listing={listing} />
            {listing.closing_comments && (
                <div className={styles.notes}>
                    <div className={styles.notesTitle}>Reseller’s Notes:</div>
                    <p className={styles.notesBody}>{listing.closing_comments}</p>
                </div>
            )}
            <div className={css.btnWrap}>
                {!withoutBtn && (
                    <div className={styles.purchaseWrap}>
                        <a href={listing.link} target="_blank" className={styles.purchaseBtn} rel="nofollow" onClick={onPurchaseClick}>
                            View it on {originNames[listing.origin]} <OwnerImage className={styles.purchaseBtnOwner} origin={listing.origin} />
                        </a>
                    </div>
                )}
                {withBidSuggestion && (
                    <button className={styles.whatOfferBtn} onClick={() => setOpen(true)}>
                        What should I offer?
                    </button>
                )}

                {/* <div className={styles.host}>
                    View it on <Owner listing={listing} />
                </div>*/}
                <small className={styles.host}>DVC For Less is not affiliated in any way with {name}</small>
            </div>
            {open && withBidSuggestion && <BidSuggestion listing={listing} onClose={() => setOpen(false)} />}
            <BugReport enabled={isBugReportEnabled} listing={listing} />
        </section>
    )
}
